// src/features/inventory/pages/ItemDetailPage/tabs/Value/index.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  History as HistoryIcon,
  Layers as LayersIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import SectionHeader from '../../../../../../components/common/SectionHeader';
import InfoTip, { ExplainTooltip, withInfo } from '../../../../../../components/common/InfoTip';
import { valuationAPI } from '../../../../services/valuation';
import { useApi } from '../../../../../../hooks/api/useApi';
import { useInventoryCostPermissions } from '../../../../../auth/hooks/usePermissions';
import { formatDKK } from '../../../../../../utils/formatters/currencyFormatter';
import { parseApiTimestamp } from '../../../../../../utils/formatters/dateFormatter';
import SetCostPriceDialog from './SetCostPriceDialog';
import PriceChart from './PriceChart';

const formatDate = (value) => parseApiTimestamp(value)?.toLocaleDateString('da-DK') ?? '—';

function StatTile({ label, value, caption, action, info }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}>
        <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
        <InfoTip title={info} />
        {action}
      </Box>
      <Typography variant="h4" fontWeight={600} lineHeight={1.2} noWrap>{value}</Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" noWrap display="block">{caption}</Typography>
      )}
    </Box>
  );
}

/**
 * What the item's stock is worth: FIFO book value, restocking price and
 * replacement value, the cost layers the stock is made of, and the cost
 * price history. Shown only with inventory:cost:read.
 *
 * An untracked item has no stock to value, so it shows only its price and
 * the price history; it stays out of the stock value reports.
 */
export default function Value({ itemId, uom, refreshKey, lotTracked = false, inventoryTracked = true }) {
  const { t } = useTranslation(['inventory', 'common']);
  const { canUpdate } = useInventoryCostPermissions();
  const { loading, error, execute, clearError } = useApi();
  const [valuation, setValuation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchValuation = useCallback(async () => {
    const result = await execute(() => valuationAPI.getItem(itemId));
    if (result.success) setValuation(result.data.data);
  }, [execute, itemId]);

  useEffect(() => {
    fetchValuation();
  }, [fetchValuation, refreshKey]);

  if (!valuation) {
    return (
      <Box sx={{ p: 4 }}>
        {error
          ? <Alert severity="error" onClose={clearError}>{error}</Alert>
          : <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
      </Box>
    );
  }

  const perUnit = (amount) => (amount != null ? `${formatDKK(amount)} / ${uom}` : '—');

  const layerColumns = [
    withInfo({
      field: 'created_at',
      headerName: t('inventory:items.tabs.value.layers.received'),
      flex: 1,
      valueFormatter: (value) => formatDate(value),
    }, t('inventory:help.value.layer_received')),
    // Only batch-tracked items have batches worth naming
    ...(lotTracked ? [{
      field: 'lot_number',
      headerName: t('inventory:items.tabs.batches.columns.lot_number'),
      flex: 1,
      renderCell: ({ row }) => row.lot_number
        ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.lot_number}</Typography>
        : row.kind === 'opening'
          ? <Typography variant="body2" color="text.secondary">{t('inventory:items.tabs.value.layers.opening')}</Typography>
          : <Typography variant="body2" color="text.secondary">—</Typography>,
    }] : []),
    withInfo({
      field: 'remaining_quantity',
      headerName: t('inventory:items.tabs.value.layers.remaining'),
      flex: 0.8,
      type: 'number',
      valueFormatter: (value, row) => `${value} / ${row.quantity}`,
    }, t('inventory:help.value.layer_remaining')),
    withInfo({
      field: 'unit_cost',
      headerName: t('inventory:items.tabs.value.layers.unit_cost'),
      flex: 1,
      type: 'number',
      valueFormatter: (value) => formatDKK(value),
    }, t('inventory:help.value.layer_unit_cost')),
    withInfo({
      field: 'remaining_value',
      headerName: t('inventory:items.tabs.value.layers.value'),
      flex: 1,
      type: 'number',
      valueFormatter: (value) => formatDKK(value),
    }, t('inventory:help.value.layer_value')),
    withInfo({
      field: 'cost_source',
      headerName: t('inventory:items.tabs.value.layers.source'),
      flex: 1,
      // Each source says where its price came from on hover
      renderCell: ({ value }) => (
        <ExplainTooltip title={t(`inventory:help.cost_sources.${value ?? 'unknown'}`)}>
          {value
            ? <Chip size="small" variant="outlined" label={t(`inventory:items.tabs.value.cost_sources.${value}`)} />
            : <Chip size="small" color="warning" variant="outlined" label={t('inventory:items.tabs.value.cost_sources.unknown')} />}
        </ExplainTooltip>
      ),
    }, t('inventory:help.value.layer_source')),
  ];

  const historyColumns = [
    {
      field: 'created_at',
      headerName: t('inventory:items.tabs.value.history.date'),
      flex: 1,
      valueFormatter: (value) => formatDate(value),
    },
    withInfo({
      field: 'cost_price',
      headerName: t('inventory:items.tabs.value.history.price'),
      flex: 1,
      type: 'number',
      valueFormatter: (value) => formatDKK(value),
    }, t('inventory:help.value.history_price')),
    withInfo({
      field: 'source',
      headerName: t('inventory:items.tabs.value.history.source'),
      flex: 1,
      renderCell: ({ value }) => (
        <ExplainTooltip title={t(`inventory:help.price_sources.${value}`)}>
          <span>{t(`inventory:items.tabs.value.price_sources.${value}`)}</span>
        </ExplainTooltip>
      ),
    }, t('inventory:help.value.history_source')),
    {
      field: 'creator',
      headerName: t('inventory:items.tabs.value.history.by'),
      flex: 1.2,
      valueGetter: (value) => value?.name ?? '—',
    },
  ];

  const editPriceButton = canUpdate && (
    <Button
      size="small"
      startIcon={<EditIcon sx={{ fontSize: 16 }} />}
      onClick={() => setDialogOpen(true)}
      sx={{ minWidth: 0, py: 0 }}
    >
      {valuation.cost_price != null ? t('common:buttons.edit') : t('inventory:items.tabs.value.set_price')}
    </Button>
  );

  const gridSx = {
    border: 'none',
    '& .MuiDataGrid-cell': { alignItems: 'center', fontVariantNumeric: 'tabular-nums' },
  };

  return (
    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {error && <Alert severity="error" onClose={clearError}>{error}</Alert>}

      {!inventoryTracked && (
        <Box>
          <SectionHeader icon={PaymentsIcon} title={t('inventory:items.tabs.value.price_title')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            <StatTile
              label={t('inventory:items.tabs.value.cost_price')}
              value={perUnit(valuation.cost_price)}
              caption={t('inventory:items.tabs.value.incl_vat')}
              info={t('inventory:help.value.cost_price_untracked')}
              action={editPriceButton}
            />
          </Box>
          <Alert severity="info" sx={{ mt: 3 }}>
            {t('inventory:items.tabs.value.untracked_note')}
          </Alert>
        </Box>
      )}

      {inventoryTracked && <Box>
        <SectionHeader icon={PaymentsIcon} title={t('inventory:items.tabs.value.summary_title')} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          <StatTile
            label={t('inventory:items.tabs.value.book_value')}
            value={formatDKK(valuation.book_value)}
            caption={t('inventory:items.tabs.value.book_value_caption', { count: valuation.quantity, uom })}
            info={t('inventory:help.value.book_value')}
          />
          <StatTile
            label={t('inventory:items.tabs.value.average_cost')}
            value={perUnit(valuation.average_unit_cost)}
            info={t('inventory:help.value.average_cost')}
          />
          <StatTile
            label={t('inventory:items.tabs.value.cost_price')}
            value={perUnit(valuation.cost_price)}
            caption={t('inventory:items.tabs.value.incl_vat')}
            info={t('inventory:help.value.cost_price')}
            action={editPriceButton}
          />
          <StatTile
            label={t('inventory:items.tabs.value.replacement_value')}
            value={formatDKK(valuation.replacement_value)}
            caption={t('inventory:items.tabs.value.replacement_caption')}
            info={t('inventory:help.value.replacement_value')}
          />
        </Box>

        {valuation.uncosted_quantity > 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {t('inventory:items.tabs.value.uncosted_warning', { count: valuation.uncosted_quantity, uom })}
          </Alert>
        )}
      </Box>}

      {inventoryTracked && <Box>
        <SectionHeader icon={LayersIcon} title={t('inventory:items.tabs.value.layers.title')} info={t('inventory:help.value.layers')} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('inventory:items.tabs.value.layers.description')}
        </Typography>
        <DataGrid
          rows={valuation.open_layers}
          columns={layerColumns}
          getRowId={(row) => row.stock_valuation_layer_id}
          loading={loading}
          disableRowSelectionOnClick
          hideFooter={valuation.open_layers.length <= 25}
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          localeText={{ noRowsLabel: t('inventory:items.tabs.value.layers.empty') }}
          autoHeight
          sx={gridSx}
        />
      </Box>}

      <Box>
        <SectionHeader icon={HistoryIcon} title={t('inventory:items.tabs.value.history.title')} />
        <PriceChart history={valuation.price_history} uom={uom} />
        <DataGrid
          rows={valuation.price_history.map((entry, index) => ({ id: index, ...entry }))}
          columns={historyColumns}
          loading={loading}
          disableRowSelectionOnClick
          hideFooter
          localeText={{ noRowsLabel: t('inventory:items.tabs.value.history.empty') }}
          autoHeight
          sx={gridSx}
        />
      </Box>

      <SetCostPriceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={setValuation}
        itemId={itemId}
        uom={uom}
        currentPrice={valuation.cost_price}
        inventoryTracked={inventoryTracked}
        uncostedQuantity={valuation.uncosted_quantity}
        filledQuantity={valuation.open_layers
          .filter((layer) => layer.cost_source === 'filled')
          .reduce((sum, layer) => sum + layer.remaining_quantity, 0)}
      />
    </Box>
  );
}
